'use client';

import React, { useEffect, useRef } from 'react';


export default function HeroSection() {
  const headlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headlineRef?.current;
    if (el) {
      setTimeout(() => el?.classList?.add('is-visible'), 100);
    }
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-24 sm:pt-20 pb-16 px-4 sm:px-6 overflow-hidden">
      {/* Background radial gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(124,58,237,0.12) 0%, transparent 70%)',
        }}
      />

      {/* Dot grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'radial-gradient(rgba(124,58,237,0.4) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black, transparent)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto w-full flex flex-col items-center text-center gap-8">

        {/* Badge */}
        <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass-panel border border-[rgba(124,58,237,0.25)]">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-light status-pulse" />
          <span className="text-xs font-mono text-violet-light tracking-widest uppercase">Live Portfolio Sync · 2,400+ landlords</span>
        </div>

        {/* Headline */}
        <div ref={headlineRef} className="reveal-group stagger-children">
          <h1
            className="font-manrope font-extrabold tracking-tighter leading-[0.95] reveal-blur"
            style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}
          >
            <span
              className="text-light-scan block"
              style={{ paddingBottom: '0.18em', marginBottom: '-0.14em' }}
            >
              Real Estate
            </span>
            <span
              className="text-light-scan block"
              style={{ animationDelay: '0.3s', paddingBottom: '0.18em', marginBottom: '-0.14em' }}
            >
              Software
            </span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                paddingBottom: '0.18em',
                marginBottom: '-0.14em',
              }}
            >
              Reimagined.
            </span>
          </h1>
        </div>

        {/* Subheadline */}
        <p className="max-w-2xl text-base sm:text-lg text-[#9999A8] font-light leading-relaxed px-2 sm:px-0">
          Lease expirations, maintenance tickets, rent collection, investor reports — pulled from every property in your portfolio into a single pane of glass that refreshes before your morning coffee cools.
        </p>

        {/* CTA Row */}
        <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 w-full px-2 sm:px-0">
          <a
            href="#demo"
            className="cta-primary inline-flex items-center gap-2.5 px-7 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-bold tracking-tight shadow-violet-glow w-full sm:w-auto justify-center"
          >
            See Your Portfolio Live
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <a
            href="#walkthrough"
            className="cta-secondary inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base w-full sm:w-auto justify-center"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
            </svg>
            <span className="hidden xs:inline">Watch the 90-Second Walkthrough</span>
            <span className="xs:hidden">Watch the Walkthrough</span>
          </a>
        </div>

        {/* Dashboard Screenshot */}
        <div className="relative w-full max-w-5xl mt-6">
          {/* Violet glow beneath screenshot */}
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] h-24 violet-pulse pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.5) 0%, transparent 70%)',
              filter: 'blur(30px)',
            }}
          />

          {/* Screenshot frame */}
          <div
            className="relative float-screenshot rounded-2xl overflow-hidden border border-[rgba(124,58,237,0.3)] shadow-[0_40px_120px_rgba(0,0,0,0.8),0_0_60px_rgba(124,58,237,0.2)]"
            style={{ background: '#1C1C24' }}
          >
            {/* Scan line */}
            <div className="scan-line" />

            {/* Fake browser chrome */}
            <div className="flex items-center gap-2 px-4 h-10 border-b border-[rgba(255,255,255,0.05)] bg-[#13131A]">
              <div className="w-3 h-3 rounded-full bg-[#3A3A4A]" />
              <div className="w-3 h-3 rounded-full bg-[#3A3A4A]" />
              <div className="w-3 h-3 rounded-full bg-[#3A3A4A]" />
              <div className="flex-1 mx-4 h-5 rounded bg-[#0D0D12] border border-[rgba(255,255,255,0.04)] flex items-center px-3">
                <span className="text-[10px] font-mono text-[#5A5A6E]">app.propmatic.io/portfolio</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                <span className="text-[10px] font-mono text-[#5A5A6E]">Live</span>
              </div>
            </div>

            {/* Dashboard content */}
            <DashboardPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

function DashboardPreview() {
  const properties = [
    { name: '14 Birchwood Ave', units: 24, occ: 96, color: '#22C55E' },
    { name: '88 Harbor View', units: 18, occ: 89, color: '#A78BFA' },
    { name: 'Westgate Complex', units: 42, occ: 100, color: '#22C55E' },
    { name: '3 Meridian Pl', units: 8, occ: 75, color: '#F59E0B' },
    { name: 'Sycamore Lofts', units: 16, occ: 94, color: '#22C55E' },
    { name: 'Park Ridge #2', units: 32, occ: 81, color: '#A78BFA' },
    { name: 'Ironwood Flats', units: 12, occ: 100, color: '#22C55E' },
    { name: '220 Lakeshore Dr', units: 28, occ: 57, color: '#EF4444' },
    { name: 'Summit Terrace', units: 20, occ: 90, color: '#22C55E' },
    { name: 'Crestwood Arms', units: 36, occ: 83, color: '#A78BFA' },
    { name: 'The Foundry', units: 14, occ: 93, color: '#22C55E' },
    { name: 'Elmhurst Point', units: 22, occ: 68, color: '#F59E0B' },
  ];

  const tickets = [
    { id: 'TK-041', desc: 'HVAC failure — Unit 3B, Westgate', urgency: 'critical', age: '2h' },
    { id: 'TK-039', desc: 'Leaking faucet — Unit 7A, Harbor View', urgency: 'medium', age: '1d' },
    { id: 'TK-037', desc: 'Lobby light out — 14 Birchwood', urgency: 'low', age: '3d' },
  ];

  const circumference = 2 * Math.PI * 35;
  const progress = 0.94;
  const offset = circumference * (1 - progress);

  return (
    <div className="grid grid-cols-12 bg-[#09090B] p-4 gap-4" style={{ minHeight: 360 }}>
      {/* Left: Portfolio list */}
      <div className="col-span-5 bg-[#1C1C24] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-manrope font-semibold text-[#EEEEF0] tracking-tight">Portfolio Overview</span>
          <span className="text-[10px] font-mono text-[#5A5A6E]">12 properties</span>
        </div>
        <div className="flex flex-col gap-1.5">
          {properties?.slice(0, 8)?.map((p) => (
            <div key={p?.name} className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-[#2A2A36] transition-colors cursor-default">
              <div className="occ-dot shrink-0" style={{ background: p?.color }} />
              <span className="text-[11px] text-[#9999A8] flex-1 truncate font-mono">{p?.name}</span>
              <span className="text-[10px] font-mono text-[#5A5A6E]">{p?.units}u</span>
              <span className="text-[10px] font-mono ml-1" style={{ color: p?.color }}>{p?.occ}%</span>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-2 border-t border-[rgba(255,255,255,0.04)] flex items-center justify-between">
          <span className="text-[10px] font-mono text-[#5A5A6E]">272 total units</span>
          <span className="text-[10px] font-mono text-[#A78BFA]">↑ 3 expiring in 30d</span>
        </div>
      </div>
      {/* Center: Rent collection ring */}
      <div className="col-span-3 bg-[#1C1C24] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col items-center justify-center gap-3">
        <span className="text-[10px] font-manrope font-semibold text-[#9999A8] tracking-widest uppercase">Rent Collected</span>
        <div className="relative flex items-center justify-center">
          <svg width="90" height="90" viewBox="0 0 90 90">
            <circle cx="45" cy="45" r="35" fill="none" stroke="#2A2A36" strokeWidth="6" />
            <circle
              cx="45" cy="45" r="35"
              fill="none"
              stroke="#7C3AED"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={offset}
              transform="rotate(-90 45 45)"
              style={{ filter: 'drop-shadow(0 0 8px rgba(124,58,237,0.6))' }}
            />
          </svg>
          <div className="absolute flex flex-col items-center">
            <span className="text-2xl font-manrope font-bold text-[#EEEEF0]">94%</span>
            <span className="text-[9px] font-mono text-[#5A5A6E]">Feb 2026</span>
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-mono font-bold text-[#22C55E]">$218,400</div>
          <div className="text-[10px] font-mono text-[#5A5A6E]">of $232,200 due</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-[#F59E0B]">6 outstanding</span>
        </div>
      </div>
      {/* Right: Maintenance queue */}
      <div className="col-span-4 bg-[#1C1C24] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-manrope font-semibold text-[#EEEEF0] tracking-tight">Maintenance Queue</span>
          <span className="px-2 py-0.5 rounded-full bg-[#EF444420] text-[10px] font-mono text-[#EF4444]">3 open</span>
        </div>
        <div className="flex flex-col gap-2">
          {tickets?.map((t) => (
            <div key={t?.id} className="flex items-start gap-2.5 p-2 rounded-lg bg-[#13131A] border border-[rgba(255,255,255,0.03)]">
              <div className={`w-1 rounded-full self-stretch shrink-0 urgency-${t?.urgency}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono text-[#A78BFA] mb-0.5">{t?.id}</div>
                <div className="text-[11px] text-[#9999A8] leading-tight truncate">{t?.desc}</div>
              </div>
              <span className="text-[9px] font-mono text-[#5A5A6E] shrink-0">{t?.age}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-2 border-t border-[rgba(255,255,255,0.04)]">
          <div className="flex items-center justify-between text-[10px] font-mono text-[#5A5A6E]">
            <span>Avg resolution: 1.4d</span>
            <span className="text-[#22C55E]">↓ 18% this month</span>
          </div>
        </div>
      </div>
    </div>
  );
}